// @flow
import React, { Component } from 'react';
import styled, { withTheme } from 'styled-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import Radio from 'common/domain/radio';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import Card from 'common/components/Card';
import Page from 'statsradio/components/page/Page';
import Button from 'common/components/ui/Button';
import ConfirmationDialog from 'common/components/ui/ConfirmationDialog';

import AddCustomerDialog from './components/AddCustomerDialog';
import EditCustomerDialog from './components/EditCustomerDialog';
import CustomerList from './components/CustomerList';
import RadioCustomer from './types/RadioCustomer';
import { getRadio, getToken } from '../authentication/session/reducers/sessionReducer';
import CustomersAPI from './services/CustomersAPI';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const ErrorWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const Row = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.metrics.largeMargin}px;
`;

type Props = {
  invitation: {
    error: boolean,
  },
  radio: Radio,
  token: string,

  t: any,
  actions: {
    openSnackbar: (type: string, message: string) => mixed,
    fetchCustomers: () => mixed,
  },
  theme: any,
};

type State = {
  error: boolean,
  fetching: boolean,
  customers: RadioCustomer[],
  showAddCustomerDialog: boolean,
  editCustomer: {
    showDialog: boolean,
    customer: ?RadioCustomer,
  },
  deleteCustomer: {
    showDialog: boolean,
    customer: ?RadioCustomer,
  }
};

class CustomersPage extends Component<Props, State> {
  constructor() {
    super();

    this.state = {
      error: false,
      fetching: false,
      customers: [],
      showAddCustomerDialog: false,
      editCustomer: {
        showDialog: false,
        customer: undefined,
      },
      deleteCustomer: {
        showDialog: false,
        customer: undefined,
      },
    };
  }

  fetchCustomers = async () => {
    this.setState({ fetching: true, error: false });
    const { token, radio: { callSign } } = this.props;
    try {
      const customers = await CustomersAPI.fetchCustomersForStation(callSign, token);
      this.setState({ fetching: false, customers: customers });
    } catch (error) {
      this.setState({ fetching: false, error: true });
    }
  };

  deleteCustomer = (customer: RadioCustomer) => {
    this.setState({ deleteCustomer: { customer, showDialog: true } });
  };


  closeDeleteDialog = () => {
    this.setState({ deleteCustomer: { showDialog: false, customer: undefined } });
  };

  onDeleteCustomerConfirmed = async () => {
    const { t, token, radio: { callSign } } = this.props;
    const { deleteCustomer: { customer } } = this.state;

    try {
      if (customer) {
        await CustomersAPI.deleteCustomer(callSign, token, customer);
        const customers = this.state.customers.filter(c => !c.isSameAs(customer));
        this.setState({ customers });
        this.props.actions.openSnackbar('message', t('deletedCustomerSuccess'));
      }
    } catch (error) {
      this.props.actions.openSnackbar('error', t('couldNotDeleteCustomer'));
    }

    this.closeDeleteDialog();
  };

  componentDidMount() {
    this.fetchCustomers();
  }

  componentDidUpdate(nextProps: Props) {
    if (this.props.radio !== nextProps.radio) {
      this.fetchCustomers();
    }
  }

  onAddCustomerDialogOpen = (): void => {
    this.setState({ showAddCustomerDialog: true });
  };

  onAddCustomerDialogClose = (): void => {
    this.setState({ showAddCustomerDialog: false });
  }

  onAddCustomerSuccess = (customer: RadioCustomer): void => {
    this.setState({ showAddCustomerDialog: false, customers: this.state.customers.concat([customer]) });
  };

  onEditCustomerDialogOpen = (customer: RadioCustomer): void => {
    this.setState({
      editCustomer: { showDialog: true, customer: customer },
    });
  };

  onEditCustomerDialogClose = (): void => {
    this.setState({
      editCustomer: { showDialog: false, customer: undefined },
    });
  };

  onEditSuccess = (radioCustomers: RadioCustomer[]): void => {
    this.setState({ customers: radioCustomers, editCustomer: { showDialog: false, customer: undefined } });
  };

  renderError(error: string) {
    const { theme } = this.props;

    return (
      <Card style={{ marginTop: theme.metrics.largeMargin }}>
        <ErrorWrapper style={theme.fonts.style.main}>{error}</ErrorWrapper>
      </Card>
    );
  }

  render() {
    const { t } = this.props;
    const { error, fetching } = this.state;

    return (
      <Page title={t('customersPageTitle')} useThemeV2>
        <Wrapper>
          <Row>
            <div className="col-lg-3 col-lg-offset-1 col-xs-4">
              {!error && <Button variant="contained" color="primary" onClick={this.onAddCustomerDialogOpen}>{t('createACustomer')}</Button>}
            </div>
          </Row>
          <Row>
            <div className="col-lg-10 col-lg-offset-1 col-xs-12">
              {!error &&
              <CustomerList
                customers={this.state.customers}
                onEditOpen={this.onEditCustomerDialogOpen}
                onDeleteCustomer={this.deleteCustomer}
                loading={fetching}
              />
              }
              {error && this.renderError(t('couldNotLoadCustomers'))}
            </div>
          </Row>
        </Wrapper>
        <AddCustomerDialog onClose={this.onAddCustomerDialogClose} onAddCustomer={this.onAddCustomerSuccess} visible={this.state.showAddCustomerDialog} />
        <ConfirmationDialog
          open={this.state.deleteCustomer.showDialog}
          onCancel={this.closeDeleteDialog}
          onConfirm={this.onDeleteCustomerConfirmed}
          action={t('deleteCustomer')}
        />
        {this.state.editCustomer.customer &&
        <EditCustomerDialog
          onEditSuccess={this.onEditSuccess}
          onClose={this.onEditCustomerDialogClose}
          visible={this.state.editCustomer.showDialog}
          customer={this.state.editCustomer.customer}
        />}
      </Page>
    );
  }
}

function mapStateToProps(state): Object {
  return {
    invitation: {
      error: state.customers.customerInvitation.error,
    },
    radio: getRadio(state),
    token: getToken(state),
  };
}

function mapDispatchToProps(dispatch: *): Object {
  return {
    actions: bindActionCreators({ openSnackbar }, dispatch),
  };
}

export default withTheme(translate('customers')(connect(mapStateToProps, mapDispatchToProps)(CustomersPage)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/customers/CustomersPage.js